import React, { FunctionComponent, useEffect, useState } from 'react';
import _ from 'lodash';
import clsx from 'clsx';
import { createStyles, makeStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import { AutoSizer, Column, Table, TableCellRenderer, TableHeaderProps, InfiniteLoader } from 'react-virtualized';
import moment from 'moment';
import { useHistory } from 'react-router';
import BackToTopArrow from '@material-ui/icons/ArrowUpward';
import { Skeleton } from '@material-ui/lab';
import { Box, Button, Fab, Tooltip, CircularProgress } from '@material-ui/core';
import ArrowUp from '@material-ui/icons/ArrowDropUp';
import ArrowDown from '@material-ui/icons/ArrowDropDown';
import gray from '@material-ui/core/colors/grey';
import { GetApp as DownloadIcon } from '@material-ui/icons';
import {
    Application,
    ApplicationList as ApplicationListType,
    LicenseListFiltersType,
    LicenseColumnConfigurationType,
} from '../../../store/modules/application/types';
import { BASE_URL } from '../../../routes';
import AddPersonDialog from '../AddPersonDialog';
import Modal from '../../../components/Modal';

declare module '@material-ui/core/styles/withStyles' {
    // Augment the BaseCSSProperties so that we can control jss-rtl
    interface BaseCSSProperties {
        /*
         * Used to control if the rule-set should be affected by rtl transformation
         */
        flip?: boolean;
    }
}

type LicenseProps = {
    applicationList: ApplicationListType;
    isLoading: boolean;
    isFilteredLoading: boolean;
    pageNumber: number;
    numberOfRows: number;
    getApplicationList: Function;
    applyFilters: Function;
    setPageNumber: Function;
    loadMore: boolean;
    licensesCount: number;
    listFilters: LicenseListFiltersType;
    checkIsLicenseFiltersApplied: Function;
    setOrderBy: Function;
    setOrderCol: Function;
    setError: Function;
    orderBy: string;
    orderCol: string;
    exportLicenses: Function;
    columnsConfiguration?: LicenseColumnConfigurationType[];
    setLicenseColumnSort: Function;
};

interface MuiVirtualizedTableProps extends WithStyles<typeof tableStyles>, LicenseProps {
    columns: ColumnData[];
    headerHeight?: number;
    onRowClick?: () => void;
    rowCount: number;
    rowGetter: (row: Row) => Record<string, any>;
    rowHeight?: number;
    history: any;
    scrollTop: number;
    setScrollTop: Function;
    setLicenseColumnSort: Function;
}

const componentStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            padding: '0px 40px 40px 40px',
            height: '100%',
        },
        tablePaginationInfo: {
            display: 'flex',
            justifyContent: 'space-between',
            padding: '10px',
        },
        addPersonInfo: {
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '10px',
            '& span': {
                margin: '0 8px',
            },
        },
        container: {
            display: 'flex',
            width: '100%',
            backgroundColor: 'white',
        },
        fab: {
            position: 'absolute',
            bottom: 55,
            left: 'calc( 100vw - 100px)',
            zIndex: 3,
            backgroundColor: 'white',
        },
        searchIconContainer: {
            width: '100%',
            maxHeight: 300,
            color: 'gray',
            paddingTop: '40px',
            paddingBottom: '150px',
        },
        noResultFound: {
            fontSize: '20px',
            color: '#484848',
            lineHeight: '24px',
            fontWeight: 500,
            marginTop: '30vh',
        },
        countSpan: {
            color: '#484848',
            fontSize: 14,
            letterSpacing: 0.1,
            display: 'flex',
            alignItems: 'center',
        },
        loadingBar: {
            width: 'calc(100vw - 100px)',
            position: 'absolute',
            bottom: 45,
            zIndex: 3,
            background: 'white',
            paddingLeft: 13,
            paddingBottom: 5,
        },
        addLicensee: {
            letterSpacing: 0.1,
        },
        exportLicense: {
            display: 'flex',
            fontSize: 14,
            letterSpacing: 0.1,
            alignItems: 'center',
        },
        modalBtn: {
            marginLeft: 0,
        },
        exportBtn: {
            padding: '0px',
            'text-transform': 'initial',
            fontWeight: 500,
            cursor: 'pointer',
            color: theme.palette.primary.main,
        },
        metadataContainer: {
            display: 'flex',
            justifyContent: 'space-between',
        },
    }),
);

const tableStyles = (theme: Theme) =>
    createStyles({
        flexContainer: {
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            boxSizing: 'border-box',
            zIndex: 3,
            cursor: 'pointer !important',
        },
        table: {
            '& .ReactVirtualized__Grid': {
                zIndex: 1,
                outline: 'none',
            },
            '& .ReactVirtualized__Table__headerColumn': {
                flex: '1 !important',
            },
            '& .ReactVirtualized__Table__rowColumn': {
                flex: '1 !important',
            },
            '& .ReactVirtualized__Table__headerRow': {
                flip: false,
                borderBottom: '#dadbda',
                paddingRight: theme.direction === 'rtl' ? '0 !important' : undefined,
            },
        },
        tableHeaderCell: {
            flex: '1 !important',
            backgroundColor: '#fefffe',
            fontWeight: 600,
            fontSize: '14px',
            color: '#484848',
        },
        tableCell: {
            flex: '1 !important',
            color: '#484848',
            fontWeight: 100,
            fontSize: 14,
            padding: '15px',
            cursor: 'pointer !important',
        },
        tableRow: {
            backgroundColor: '#fefffe',
            '&:hover ': {
                backgroundColor: '#eeeeee',
                transition: 'ease-in 200ms',
                '& button': {
                    opacity: 1,
                    transition: 'ease-in 200ms',
                },
            },
        },
        tableRowHover: {
            '&:hover': {
                backgroundColor: theme.palette.grey[200],
            },
        },
        noClick: {
            cursor: 'initial',
        },
        sortableIcon: {
            color: gray['300'],
            marginTop: 2,
            fontSize: 20,
            '&.active': {
                color: 'black',
            },
        },
    });

interface ColumnData {
    dataKey: string;
    label: string;
    numeric?: boolean;
    width: number;
}

interface Row {
    index: number;
}

const defaultColumnsConfiguration = [
    { name: 'ID', data: '$.id', dataType: 'text' },
    { name: 'License Number', data: '$.licenseNumber', dataType: 'text' },
    { name: 'License Type', data: '$.type.name', dataType: 'text' },
    { name: 'Date Received', data: '$.applications[0].receivedDate', dataType: 'date', format: 'L' },
    { name: 'Applicant Name', data: '$.person.name', dataType: 'text' },
    { name: 'Status', data: '$.status', dataType: 'text' },
];

class MuiVirtualizedTable extends React.PureComponent<MuiVirtualizedTableProps> {
    promiseResolve: any;
    isLoading: boolean = false;

    static defaultProps = {
        headerHeight: 48,
        rowHeight: 48,
    };

    routeChange = (path: string) => {
        const { history } = this.props;
        history.push(path);
    };

    getRowClassName = ({ index }: Row, isFilteredLoading: boolean) => {
        const { classes, onRowClick } = this.props;
        let returnObj = clsx(
            classes.tableRow,
            classes.flexContainer,
            index !== -1 && isFilteredLoading ? 'blur-loading' : '',
            {
                [classes.tableRowHover]: index !== -1 && onRowClick != null,
            },
        );
        return returnObj;
    };

    cellRenderer: TableCellRenderer = ({ cellData, columnIndex }) => {
        const { columns, classes, rowHeight, onRowClick } = this.props;
        return (
            <TableCell
                component="div"
                className={clsx(classes.tableCell, classes.flexContainer, {
                    [classes.noClick]: onRowClick == null,
                })}
                variant="body"
                style={{ height: rowHeight }}
                align={(columnIndex != null && columns[columnIndex].numeric) || false ? 'right' : 'left'}
            >
                {cellData}
            </TableCell>
        );
    };

    headerRenderer = (headerProps: TableHeaderProps & { columnIndex: number }) => {
        const { label, columnIndex } = headerProps;
        const {
            headerHeight,
            columns,
            classes,
            orderBy,
            orderCol,
            setOrderCol,
            setOrderBy,
            numberOfRows,
            checkIsLicenseFiltersApplied,
            setPageNumber,
            getApplicationList,
            listFilters,
            applyFilters,
            columnsConfiguration,
            setLicenseColumnSort,
        } = this.props;

        const sortCol = (columnsConfiguration ?? defaultColumnsConfiguration)
            .find((column) => column.name === label)
            ?.data.slice(2);

        return (
            <TableCell
                component="div"
                className={clsx(classes.tableHeaderCell, classes.flexContainer, classes.noClick)}
                variant="head"
                style={{ height: headerHeight }}
                align={columns[columnIndex].numeric || false ? 'right' : 'left'}
                onClick={() => {
                    this.isLoading = true;
                    const nextOrder = orderBy === 'DESC' ? 'ASC' : 'DESC';
                    setLicenseColumnSort(nextOrder, sortCol);
                    if (!checkIsLicenseFiltersApplied()) {
                        getApplicationList({
                            pageNumber: 0,
                            numberOfRows,
                            orderCol: sortCol,
                            orderBy: nextOrder,
                            done: () => {
                                this.isLoading = false;
                                setPageNumber(0);
                                setOrderCol(sortCol);
                                setOrderBy(nextOrder);
                            },
                        });
                    } else {
                        applyFilters(listFilters, {
                            pageNumber: 0,
                            numberOfRows,
                            orderCol: sortCol,
                            orderBy: nextOrder,
                            done: () => {
                                this.isLoading = false;
                                setPageNumber(0);
                                setOrderCol(sortCol);
                                setOrderBy(nextOrder);
                            },
                        });
                    }
                }}
            >
                <span className={'sortableIconSpan'}>
                    <span>{label}</span>
                    <span>
                        {orderCol !== sortCol && orderBy === 'DESC' && <ArrowUp className={'sortableIconhover'} />}
                        {orderCol !== sortCol && orderBy === 'ASC' && <ArrowDown className={'sortableIconhover'} />}
                    </span>
                </span>
                {orderCol === sortCol && orderBy === 'ASC' && (
                    <ArrowUp className={`${classes.sortableIcon} ${orderCol === sortCol ? 'active' : ''}`} />
                )}
                {orderCol === sortCol && orderBy === 'DESC' && (
                    <ArrowDown className={`${classes.sortableIcon} ${orderCol === sortCol ? 'active' : ''}`} />
                )}
            </TableCell>
        );
    };

    loadMore = () => {
        // simulate a request
        setTimeout(() => {
            this.actuallyLoadMore();
        }, 500);
        // we need to return a promise
        //we might need that reject veriable
        return new Promise((resolve) => {
            this.promiseResolve = resolve;
        });
    };

    actuallyLoadMore = () => {
        // resolve the promise after data where fetched
        const {
            getApplicationList,
            pageNumber,
            numberOfRows,
            setPageNumber,
            loadMore,
            listFilters,
            applyFilters,
            checkIsLicenseFiltersApplied,
            isFilteredLoading,
            orderBy,
            orderCol,
        } = this.props;
        if (!this.isLoading && !isFilteredLoading && loadMore) {
            this.isLoading = true;
            if (!checkIsLicenseFiltersApplied()) {
                getApplicationList({
                    pageNumber: pageNumber + 1,
                    numberOfRows,
                    status: true,
                    orderBy,
                    orderCol,
                    done: () => {
                        this.isLoading = false;
                        this.promiseResolve();
                        setPageNumber(pageNumber + 1);
                    },
                });
            } else {
                applyFilters(listFilters, {
                    pageNumber: pageNumber + 1,
                    numberOfRows,
                    status: true,
                    orderBy,
                    orderCol,
                    done: () => {
                        this.isLoading = false;
                        this.promiseResolve();
                        setPageNumber(pageNumber + 1);
                    },
                });
            }
        }
    };

    render() {
        const {
            classes,
            columns,
            rowHeight,
            headerHeight,
            isFilteredLoading,
            applicationList,
            setScrollTop,
            scrollTop,
            ...tableProps
        } = this.props;
        return (
            <InfiniteLoader
                isRowLoaded={({ index }) => !!applicationList[index]}
                loadMoreRows={this.loadMore}
                rowCount={10000000}
            >
                {({ onRowsRendered, registerChild }) => (
                    <AutoSizer>
                        {({ height, width }) => (
                            <Table
                                height={height}
                                width={width}
                                rowHeight={rowHeight!}
                                gridStyle={{
                                    direction: 'inherit',
                                }}
                                ref={registerChild}
                                onRowsRendered={onRowsRendered}
                                headerHeight={headerHeight!}
                                className={classes.table}
                                scrollTop={scrollTop}
                                {...tableProps}
                                rowClassName={(row) => {
                                    return this.getRowClassName(row, isFilteredLoading);
                                }}
                                onRowClick={(row) => {
                                    this.routeChange(BASE_URL + `/license/${row.rowData.id}/detail`);
                                }}
                                onScroll={(scrollParams: any) => {
                                    setScrollTop(scrollParams.scrollTop);
                                }}
                            >
                                {columns.map(({ dataKey, ...other }, index) => {
                                    return (
                                        <Column
                                            key={dataKey}
                                            headerRenderer={(headerProps) =>
                                                this.headerRenderer({
                                                    ...headerProps,
                                                    columnIndex: index,
                                                })
                                            }
                                            className={classes.flexContainer}
                                            cellRenderer={this.cellRenderer}
                                            dataKey={dataKey}
                                            {...other}
                                        />
                                    );
                                })}
                            </Table>
                        )}
                    </AutoSizer>
                )}
            </InfiniteLoader>
        );
    }
}

const VirtualizedTable = withStyles(tableStyles)(MuiVirtualizedTable);

interface Data {
    id: string;
    licenseNumber: string;
    'type.name': string;
    receivedDate: string;
    name: string;
    status: string;
}

const LicenseInfiniteList: FunctionComponent<LicenseProps> = (props) => {
    const classes = componentStyles();
    const history = useHistory();
    const [scrollTop, setScrollTop] = useState<number>(0);
    const [isValidFromDate] = useState(true);
    const [isValidToDate] = useState(true);
    const [isAddPersonModalOpen, setAddPersonModalOpen] = useState(false);
    const {
        applicationList,
        isFilteredLoading,
        isLoading,
        pageNumber,
        licensesCount,
        checkIsLicenseFiltersApplied,
        setError,
        listFilters,
        orderBy,
        orderCol,
        exportLicenses,
        columnsConfiguration,
        setLicenseColumnSort,
    } = props;
    useEffect(() => {
        if (pageNumber === 0) {
            setScrollTop(0);
        }
    }, [pageNumber]);

    const getRedefinedObj = (license: Application) => {
        const rowValues: Record<string, any> = {};
        (columnsConfiguration ?? defaultColumnsConfiguration).forEach((column) => {
            const value = _.get(license, column.data.slice(2));
            rowValues[_.camelCase(column.name)] =
                column.dataType === 'date' && !!value ? moment(value).format(column.format) : value;
        });
        rowValues.id = license?.id;
        return rowValues;
    };

    const handleAddPerson = () => {
        setAddPersonModalOpen(true);
    };

    const ExportLicensesLocal = () => {
        const momentToDate = moment(listFilters.toDate);
        const momentFromDate = moment(listFilters.fromDate);

        if (!momentToDate.isValid() || !momentFromDate.isValid() || !isValidFromDate || !isValidToDate) {
            setError({ isError: true, errorCode: 0, message: 'From Date and To Date are required.' });
        } else if (momentToDate.diff(momentFromDate, 'days') < 0) {
            setError({ isError: true, errorCode: 0, message: 'To Date cannot be before From Date.' });
        } else {
            exportLicenses({ ...listFilters, orderBy, orderCol });
        }
    };

    const getTableColumns = (configuredColumns?: LicenseColumnConfigurationType[]) => {
        return (configuredColumns ?? defaultColumnsConfiguration).map((column) => ({
            label: column.name,
            dataKey: _.camelCase(column.name),
            width: 300,
        }));
    };

    return (
        <Box className={`${classes.root} verticalFillContainer`}>
            <Box className={classes.metadataContainer}>
                <Box className={classes.tablePaginationInfo}>
                    <span className={classes.countSpan}>
                        {checkIsLicenseFiltersApplied() &&
                            (isFilteredLoading ? (
                                <>
                                    <CircularProgress size={11} /> &nbsp;&nbsp;&nbsp;
                                </>
                            ) : (
                                `${licensesCount} Results Found`
                            ))}
                    </span>
                </Box>
                <Box className={classes.addPersonInfo}>
                    <span className={classes.addLicensee}>
                        <Button
                            className={classes.modalBtn}
                            onClick={handleAddPerson}
                            variant="contained"
                            color="primary"
                        >
                            ADD LICENSEE
                        </Button>
                    </span>
                    <span className={classes.exportLicense}>
                        <Box display="flex" className={classes.exportBtn}>
                            <Box display="flex" flexDirection="column" justifyContent="center">
                                <Button
                                    className={classes.modalBtn}
                                    onClick={ExportLicensesLocal}
                                    variant="contained"
                                    color="primary"
                                >
                                    <DownloadIcon /> &nbsp;Export
                                </Button>
                            </Box>
                        </Box>
                    </span>
                </Box>
            </Box>
            <Box className={`${classes.container} verticalFillContent`}>
                <VirtualizedTable
                    rowCount={(applicationList && applicationList.length) || 0}
                    rowGetter={({ index }) => getRedefinedObj(applicationList[index])}
                    {...props}
                    history={history}
                    scrollTop={scrollTop}
                    setScrollTop={setScrollTop}
                    columns={getTableColumns(columnsConfiguration)}
                    setLicenseColumnSort={setLicenseColumnSort}
                />

                {!isLoading && !isFilteredLoading && applicationList !== undefined && applicationList.length === 0 && (
                    <Box className={classes.searchIconContainer} display="flex" flexDirection="column">
                        <Box className={classes.noResultFound} display="flex" justifyContent="center">
                            No Record Found
                        </Box>
                    </Box>
                )}
                {applicationList && applicationList.length > 11 && scrollTop > 20 && !isFilteredLoading && (
                    <Box
                        width={'100%'}
                        display="flex"
                        flexDirection="column"
                        justifyContent="space-between"
                        padding={5}
                    >
                        <Skeleton />
                        <Skeleton />
                        <Skeleton />
                        <Skeleton />
                        <Skeleton />
                        <Skeleton />
                        <Skeleton />
                        <Skeleton />
                        <Skeleton />
                        <Skeleton />
                    </Box>
                )}
            </Box>
            {isLoading && scrollTop > 20 && (
                <Box display="flex" flexDirection="column" className={classes.loadingBar}>
                    <Skeleton />
                    <Skeleton />
                </Box>
            )}
            {scrollTop > 20 && (
                <Tooltip title="Back to Top" placement="top">
                    <Fab
                        size="small"
                        className={classes.fab}
                        aria-label="edit"
                        onClick={() => {
                            setScrollTop(0);
                        }}
                    >
                        <BackToTopArrow color="primary" />
                    </Fab>
                </Tooltip>
            )}

            {isAddPersonModalOpen && (
                <Modal open={isAddPersonModalOpen} maxWidth={'sm'} fullWidth={true}>
                    <AddPersonDialog setAddPersonModalOpen={setAddPersonModalOpen} />
                </Modal>
            )}
        </Box>
    );
};

export default LicenseInfiniteList;
